
import React, { useState, useEffect, useRef } from 'react';
import { generateLessonContent } from '../services/geminiService';
import { LessonContent, UserProfile, CurriculumNode, Language, SubjectType, LessonWidget, Theme } from '../types';
import { Button } from '../components/shared';
import { Loader2, AlertCircle } from 'lucide-react';
import { translations } from '../i18n';
import { useLessonAudio } from '../hooks/useLessonAudio';
import { IntroStage, WidgetStage, QuizStage } from '../components/lesson/LessonStages';
import { LessonHeader } from '../components/lesson/LessonHeader';
import { LessonFooter } from '../components/lesson/LessonFooter';

interface LessonViewProps {
  node: CurriculumNode;
  subject: SubjectType;
  user: UserProfile;
  lang: Language;
  theme: Theme;
  onComplete: (actualSubject: SubjectType) => void;
  onExit: () => void;
}

const THEME_STYLES: Record<SubjectType, { 
    dark: { bg: string, accent: string, text: string, btnGradient: string, btnShadow: string },
    light: { bg: string, accent: string, text: string, btnGradient: string, btnShadow: string }
}> = {
  math: { 
    dark: { bg: 'from-blue-950 via-indigo-950 to-slate-950', accent: 'text-blue-300', text: 'text-white', btnGradient: 'from-blue-500 to-indigo-600', btnShadow: 'shadow-blue-500/30' },
    light: { bg: 'from-blue-50 via-indigo-50 to-white', accent: 'text-blue-600', text: 'text-slate-900', btnGradient: 'from-blue-500 to-indigo-600', btnShadow: 'shadow-blue-500/30' }
  },
  science: { 
    dark: { bg: 'from-emerald-950 via-teal-950 to-slate-950', accent: 'text-emerald-300', text: 'text-white', btnGradient: 'from-emerald-500 to-teal-600', btnShadow: 'shadow-emerald-500/30' },
    light: { bg: 'from-emerald-50 via-teal-50 to-white', accent: 'text-emerald-600', text: 'text-slate-900', btnGradient: 'from-emerald-500 to-teal-600', btnShadow: 'shadow-emerald-500/30' }
  },
  chinese: { 
    dark: { bg: 'from-red-950 via-rose-950 to-slate-950', accent: 'text-rose-300', text: 'text-white', btnGradient: 'from-red-500 to-rose-600', btnShadow: 'shadow-red-500/30' },
    light: { bg: 'from-red-50 via-rose-50 to-white', accent: 'text-rose-600', text: 'text-slate-900', btnGradient: 'from-red-500 to-rose-600', btnShadow: 'shadow-red-500/30' }
  },
  english: { 
    dark: { bg: 'from-violet-950 via-purple-950 to-slate-950', accent: 'text-violet-300', text: 'text-white', btnGradient: 'from-violet-500 to-purple-600', btnShadow: 'shadow-violet-500/30' },
    light: { bg: 'from-violet-50 via-purple-50 to-white', accent: 'text-violet-600', text: 'text-slate-900', btnGradient: 'from-violet-500 to-purple-600', btnShadow: 'shadow-violet-500/30' }
  },
  history: { 
    dark: { bg: 'from-amber-950 via-orange-950 to-slate-950', accent: 'text-amber-300', text: 'text-white', btnGradient: 'from-amber-500 to-orange-600', btnShadow: 'shadow-amber-500/30' },
    light: { bg: 'from-amber-50 via-orange-50 to-white', accent: 'text-amber-600', text: 'text-slate-900', btnGradient: 'from-amber-500 to-orange-600', btnShadow: 'shadow-amber-500/30' }
  },
  art: { 
    dark: { bg: 'from-fuchsia-950 via-pink-950 to-slate-950', accent: 'text-pink-300', text: 'text-white', btnGradient: 'from-fuchsia-500 to-pink-600', btnShadow: 'shadow-fuchsia-500/30' },
    light: { bg: 'from-fuchsia-50 via-pink-50 to-white', accent: 'text-pink-600', text: 'text-slate-900', btnGradient: 'from-fuchsia-500 to-pink-600', btnShadow: 'shadow-fuchsia-500/30' }
  },
};

export const LessonView: React.FC<LessonViewProps> = ({ node, subject, user, lang, theme, onComplete, onExit }) => {
  const [content, setContent] = useState<LessonContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [viewMode, setViewMode] = useState<'stepped' | 'scroll'>(() => user.grade >= 4 ? 'scroll' : 'stepped');
  const [step, setStep] = useState(0); 
  const [quizState, setQuizState] = useState<Record<number, number>>({});
  
  const [autoPlay, setAutoPlay] = useState(false);
  const t = translations[lang];
  const isDark = theme === 'dark';
  const styles = THEME_STYLES[subject][isDark ? 'dark' : 'light'];

  const { isPlaying, loading: audioLoading, play, stop } = useLessonAudio(autoPlay);

  useEffect(() => {
    let active = true;
    const init = async () => {
      setLoading(true);
      setError(null);
      setIsStreaming(true);
      try {
        const result = await generateLessonContent(node.id, node.title, subject, user, lang, (partial) => {
          if (active && (partial.title || partial.intro_story)) {
            setLoading(false); // Stop loading as soon as we have a title or story
            setContent(prev => ({ ...prev, ...partial } as LessonContent));
          }
        });
        if (active && result) {
          setContent(result);
          setLoading(false);
          setIsStreaming(false);
        }
      } catch (e: any) {
        console.error("Lesson generation failed", e);
        if (active) {
            setError(e.message || "Failed to launch mission.");
            setLoading(false);
            setIsStreaming(false);
        }
      } finally {
        if (active) setIsStreaming(false);
      }
    };
    init();
    return () => { active = false; stop(); };
  }, [node.id]);

  const widgets = content?.widgets || [];
  const hasQuiz = !!(content?.quiz && content.quiz.length > 0);
  const totalSteps = 1 + widgets.length + (hasQuiz ? 1 : 0);
  const isIntro = step === 0;
  const isQuiz = hasQuiz && step === totalSteps - 1;
  const currentWidget = (!isIntro && !isQuiz) ? widgets[step - 1] : null;

  const lastPlayedText = useRef<string>("");

  useEffect(() => {
    if (viewMode !== 'stepped' || !content || loading || isQuiz) return;
    if (!autoPlay) return;

    let textToRead = "";
    if (isIntro) {
      textToRead = content.intro_story || "";
    } else if (currentWidget) {
       if (currentWidget.type === 'text') {
         textToRead = typeof currentWidget.data === 'string' ? currentWidget.data : (currentWidget.data?.content || "");
       } else if (currentWidget.type === 'visual_image') {
         textToRead = currentWidget.data?.caption || "";
       } else if (currentWidget.type === 'vocab_card') {
         textToRead = `${currentWidget.data?.word || ''}. ${currentWidget.data?.meaning || ''}`;
       } else if (currentWidget.type === 'did_you_know') {
         textToRead = `${t.didYouKnow}: ${currentWidget.data || ''}`;
       } else if (currentWidget.type === 'magic_formula') {
         textToRead = typeof currentWidget.data === 'string' ? currentWidget.data : (currentWidget.data?.content || "");
       }
    }

    if (textToRead && textToRead.trim() !== "" && textToRead !== lastPlayedText.current) {
       lastPlayedText.current = textToRead;
       play(textToRead, () => {
          if (step < totalSteps - 1) setStep(s => s + 1);
       });
    }
  }, [step, content, loading, isQuiz, autoPlay, viewMode]);

  const handleStartAdventure = () => {
    if (!content?.intro_story) {
        setStep(1);
        return;
    }
    setAutoPlay(true);
    const textToRead = content.intro_story;
    lastPlayedText.current = textToRead;
    play(textToRead, () => {
       if (step < totalSteps - 1) setStep(s => s + 1);
    });
  };

  const handleFinish = () => {
    const finalSubject = (content?.category as SubjectType) || subject;
    onComplete(finalSubject);
  };

  if (loading && !content) {
    return (
      <div className={`fixed inset-0 z-[60] flex flex-col items-center justify-center bg-gradient-to-br ${styles.bg} ${isDark ? 'text-white' : 'text-slate-900'}`}>
        <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mb-6" />
        <h2 className="text-3xl font-black font-orbitron mt-4 mb-2 tracking-widest animate-pulse uppercase">{t.generating}</h2>
      </div>
    );
  }

  if (error) {
      return (
        <div className={`fixed inset-0 z-[60] flex flex-col items-center justify-center bg-slate-900 text-white p-8 text-center`}>
            <AlertCircle className="w-16 h-16 text-rose-500 mb-6" />
            <h2 className="text-2xl font-black uppercase mb-4 tracking-widest">Mission Failed</h2>
            <p className="text-slate-400 mb-8 max-w-md">{error}</p>
            <Button onClick={onExit} variant="secondary">Back to Base</Button>
        </div>
      );
  }

  const progressPercent = viewMode === 'stepped' ? ((step + 1) / totalSteps) * 100 : 100;
  const quizCompleted = content?.quiz ? Object.keys(quizState).length >= content.quiz.length : true;

  return (
    <div className={`fixed inset-0 z-[50] overflow-y-auto bg-white dark:bg-slate-950 ${isDark ? 'text-white' : 'text-slate-900'} animate-in fade-in duration-500`}>
      <div className="max-w-5xl mx-auto min-h-screen flex flex-col relative z-10">
        
        <LessonHeader 
          onExit={onExit} 
          viewMode={viewMode} 
          setViewMode={setViewMode} 
          progressPercent={progressPercent} 
          isStreaming={isStreaming} 
          autoPlay={autoPlay} 
          setAutoPlay={setAutoPlay} 
          isDark={isDark} 
          lang={lang} 
        />

        {viewMode === 'scroll' && content && (
           <div className="flex-1 px-4 md:px-8 py-8 space-y-12 pb-32">
               <IntroStage title={content.title || node.title} concept={content.intro_story || "..."} styles={styles} subject={subject} nodeId={node.id} onPlayAudio={play} isDark={isDark} isIntro onStartAdventure={handleStartAdventure} />
               {content.widgets.map((w, i) => <WidgetStage key={i} widget={w} styles={styles} subject={subject} nodeId={node.id} onPlayAudio={play} isDark={isDark} />)}
               {!isStreaming && hasQuiz && <QuizStage quiz={content.quiz!} quizState={quizState} setQuizState={setQuizState} t={t} isDark={isDark} />}
               {!isStreaming && <div className="flex justify-center pt-8"><Button onClick={handleFinish} disabled={!quizCompleted} className="px-12 py-6 text-xl rounded-full">{t.completeMission}</Button></div>}
           </div>
        )}

        {viewMode === 'stepped' && content && (
          <>
            <div className="flex-1 flex flex-col justify-center px-4 py-8 md:px-12">
                <div className="flex-1 flex flex-col items-center justify-center">
                    {isIntro ? (
                        <IntroStage 
                          title={content.title || node.title} 
                          concept={content.intro_story || "..."} 
                          styles={styles} 
                          subject={subject} 
                          nodeId={node.id} 
                          onPlayAudio={play} 
                          isDark={isDark} 
                          isIntro 
                          onStartAdventure={handleStartAdventure}
                          audioLoading={audioLoading}
                          isPlaying={isPlaying}
                        />
                    ) : isQuiz ? (
                        <QuizStage quiz={content.quiz!} quizState={quizState} setQuizState={setQuizState} t={t} isDark={isDark} />
                    ) : (currentWidget && currentWidget.data) ? (
                        <WidgetStage widget={currentWidget} styles={styles} subject={subject} nodeId={node.id} onPlayAudio={play} isDark={isDark} />
                    ) : (
                        <div className="animate-pulse flex flex-col items-center">
                            <Loader2 className="w-16 h-16 text-indigo-500 animate-spin mb-6" />
                            <p className="text-lg font-black text-slate-400 uppercase tracking-widest">{t.thinking}</p>
                        </div>
                    )}
                </div>
            </div>

            <LessonFooter 
              step={step} 
              totalSteps={totalSteps} 
              onPrev={() => setStep(s => Math.max(0, s - 1))} 
              onNext={() => setStep(s => Math.min(totalSteps - 1, s + 1))} 
              onComplete={handleFinish}
              canGoNext={step < totalSteps - 1 && (isIntro ? (!!content.intro_story || !isStreaming) : !!(currentWidget && currentWidget.data))}
              isQuiz={isQuiz}
              isWaitingForMore={isStreaming && step >= widgets.length}
              quizCompleted={quizCompleted}
              lang={lang}
              isDark={isDark}
            />
          </>
        )}
      </div>
    </div>
  );
};
